import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import TrialBadge from './TrialBadge';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = ({ onLoginClick, transparent = false }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const showTransparent = transparent && isHome && !scrolled;
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showTransparent ? 'bg-transparent' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1" data-testid="navbar-logo">
              <span
                className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${
                  showTransparent ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                khadam<span className="text-blue-500">express</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-5">
              <LanguageSwitcher light={showTransparent} />
              <Link
                to="/devenir-prestataire"
                data-testid="navbar-become-provider"
                className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                  showTransparent ? 'text-white' : 'text-gray-700'
                }`}
              >
                {t('nav.becomeProvider')}
              </Link>
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      data-testid="navbar-admin-link"
                      className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors duration-300 ${
                        showTransparent ? 'text-white border-white/30 hover:bg-white/10' : 'text-red-600 border-red-200 hover:bg-red-50'
                      }`}
                    >
                      Admin
                    </Link>
                  )}
                  {(user?.role === 'client' || user?.role === 'provider') && (
                    <Link
                      to="/messages"
                      data-testid="navbar-messages-link"
                      className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                        showTransparent ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      {t('nav.messages')}
                    </Link>
                  )}
                  {user?.role === 'provider' && (
                    <Link
                      to="/dashboard"
                      data-testid="navbar-dashboard-link"
                      className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                        showTransparent ? 'text-white' : 'text-blue-600'
                      }`}
                    >
                      {t('nav.dashboard')}
                    </Link>
                  )}
                  {user?.role === 'client' && (
                    <Link
                      to="/mes-reservations"
                      data-testid="navbar-my-bookings-link"
                      className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                        showTransparent ? 'text-white' : 'text-blue-600'
                      }`}
                    >
                      {t('nav.myBookings')}
                    </Link>
                  )}
                  {user?.role === 'provider' && user?.trial_active && user?.free_trial_end_date && (
                    <TrialBadge trialEndDate={user.free_trial_end_date} />
                  )}
                  <span className={`text-sm font-medium ${showTransparent ? 'text-white' : 'text-gray-700'}`}>
                    {user?.name}
                  </span>
                  <button
                    onClick={logout}
                    data-testid="navbar-logout-btn"
                    aria-label={t('nav.logout')}
                    className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                      showTransparent ? 'text-white' : 'text-gray-700'
                    }`}
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={onLoginClick}
                  data-testid="navbar-login-btn"
                  className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                    showTransparent ? 'text-white' : 'text-gray-700'
                  }`}
                >
                  {t('nav.loginRegister')}
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSwitcher light={showTransparent} />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="navbar-mobile-toggle"
                aria-label="Menu"
                className={`p-2 rounded-lg transition-colors ${
                  showTransparent
                    ? 'text-white hover:bg-white/10'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-3 space-y-3">
              <Link
                to="/devenir-prestataire"
                className="block text-sm font-medium text-gray-700 hover:text-blue-600 py-2"
                onClick={() => setMobileOpen(false)}
              >
                {t('nav.becomeProvider')}
              </Link>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  onLoginClick?.();
                }}
                className="block text-sm font-medium text-gray-700 hover:text-blue-600 py-2 w-full text-left"
              >
                {t('nav.loginRegister')}
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
